import React, { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import Navbar from "../../Header/header";
import SASidebar from "../../Dashboard/SuperAdmin/Superadmincom/SuperAdminSidebar";
import { API_LoginURL,API_BannerURL } from "../../../config";
import Swal from "sweetalert2";
import ReactPaginate from "react-paginate";
import { RotatingLines } from "react-loader-spinner";
import { debounce } from "lodash";

// Constants for banner locations
const bannerLocations = ["Homepage", "Admin Dashboard", "Student Dashboard"];
const bannerPageLocations = [
  "Left Upper",
  "Middle Upper",
  "Right Upper",
  "Middle Left Middle",
  "Right Middle",
  "Left Lower",
  "Middle Lower",
  "Right Lower",
  "Header",
  "Footer",
  "Left Sidebar",
  "Right Sidebar",
];

export default function BannerManagement() {
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);

  // State management
  const [state, setState] = useState({
    banners: [],
    loading: true,
    formSubmitting: false,
    formData: {
      bannerId: 0,
      bannerTitle: "",
      bannerDescription: "",
      bannerLocation: "",
      bannerPageLocation: "",
      bannerLink: "",
      bannerPath: "",
      bannerStatus: true,
      isDeleted: false,
      formFile: null,
    },
    searchQuery: "",
    filterStatus: "all",
    currentPage: 0,
    itemsPerPage: 5,
  });

  // Helper function to update state
  const updateState = (newState) => {
    setState((prev) => ({ ...prev, ...newState }));
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchBanners();
  }, []);

  // API calls
  const fetchBanners = async () => {
    updateState({ loading: true });
    try {
      const response = await axios.get(
        `${API_LoginURL}banner/BannerMasterList`,
        {
          headers: getHeaders(),
        }
      );
      updateState({
        banners: Array.isArray(response.data) ? response.data : [],
        loading: false,
      });
    } catch (error) {
      console.error("Error fetching banners:", error);
      Swal.fire("Error!", "Failed to load banners.", "error");
      updateState({ loading: false });
    }
  };

  // Helper function for headers
  const getHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: localStorage.getItem("token"),
  });

  // Form handlers
  const handleInputChange = (e) => {
    const { name, value, files, type, checked } = e.target;

    updateState({
      formData: {
        ...state.formData,
        [name]: type === "checkbox" ? checked : files ? files[0] : value,
      },
    });
  };

  // Debounced search
  const debouncedSearch = useMemo(
    () =>
      debounce((query) => {
        updateState({ searchQuery: query, currentPage: 0 });
      }, 300),
    []
  );

  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value);
  };

  const handleFilterChange = (e) => {
    updateState({
      filterStatus: e.target.value,
      currentPage: 0,
    });
  };

  const resetForm = () => {
    updateState({
      formData: {
        bannerId: 0,
        bannerTitle: "",
        bannerDescription: "",
        bannerLocation: "",
        bannerPageLocation: "",
        bannerLink: "",
        bannerPath: "",
        bannerStatus: true,
        isDeleted: false,
        formFile: null,
      },
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleEdit = (banner) => {
    updateState({
      formData: {
        ...banner,
        bannerStatus: banner.bannerStatus !== false,
        formFile: null, // Reset file input when editing
      },
    });
  };

  const handleDelete = async (bannerId) => {
    const confirmation = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (!confirmation.isConfirmed) return;

    try {
      await axios.post(
        `${API_LoginURL}banner/DeleteBannerMaster`,
        { bannerMasterId: bannerId },
        { headers: getHeaders() }
      );
      updateState({
        banners: state.banners.filter((item) => item.bannerId !== bannerId),
      });
      Swal.fire("Deleted!", "Banner has been deleted.", "success");
    } catch (error) {
      console.error("Error deleting banner:", error);
      Swal.fire("Error", "Failed to delete banner", "error");
    }
  };

  const handlePageChange = ({ selected }) => {
    updateState({ currentPage: selected });
  };

  const handleFormSubmit = async () => {
    // Validate required fields
    if (
      !state.formData.bannerTitle ||
      !state.formData.bannerLocation ||
      !state.formData.bannerPageLocation ||
      (state.formData.bannerId === 0 && !state.formData.formFile)
    ) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please fill all required fields and upload an image for new banners",
      });
      return;
    }

    updateState({ formSubmitting: true });

    try {
      const formData = new FormData();
      // Append all form data to FormData object
      Object.keys(state.formData).forEach((key) => {
        if (
          key === "formFile" &&
          state.formData.bannerId > 0 &&
          !state.formData.formFile
        ) {
          // Skip file if editing and no new file was selected
          return;
        }
        if (state.formData[key] !== null && state.formData[key] !== undefined) {
          formData.append(key, state.formData[key]);
        }
      });

      const response = await axios.post(
        `${API_LoginURL}banner/BannerAddEdit`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: localStorage.getItem("token"),
          },
        }
      );

      if (response.data.response === "SUCCESS") {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Banner saved successfully",
        });
        fetchBanners();
        resetForm();
        // Close modal using Bootstrap's modal method
        if (modalRef.current) {
          const modal = window.bootstrap.Modal.getInstance(modalRef.current);
          if (modal) modal.hide();
        }
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.data.response || "Failed to save banner",
        });
      }
    } catch (error) {
      console.error("Error saving banner:", error);
      Swal.fire("Error", "Failed to save banner", "error");
    } finally {
      updateState({ formSubmitting: false });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Filter and pagination
  const filteredBanners = state.banners.filter((item) => {
    const matchesSearch =
      item.bannerTitle
        ?.toLowerCase()
        .includes(state.searchQuery.toLowerCase()) ||
      item.bannerDescription
        ?.toLowerCase()
        .includes(state.searchQuery.toLowerCase()) ||
      item.bannerLocation
        ?.toLowerCase()
        .includes(state.searchQuery.toLowerCase()) ||
      item.bannerPageLocation
        ?.toLowerCase()
        .includes(state.searchQuery.toLowerCase());

    const matchesStatus =
      state.filterStatus === "all" ||
      (state.filterStatus === "active" && item.bannerStatus) ||
      (state.filterStatus === "inactive" && !item.bannerStatus);

    return matchesSearch && matchesStatus;
  });

  const pageCount = Math.ceil(filteredBanners.length / state.itemsPerPage);
  const displayedBanners = filteredBanners.slice(
    state.currentPage * state.itemsPerPage,
    (state.currentPage + 1) * state.itemsPerPage
  );

  return (
    <div className="container-scroller">
      <Navbar />
      <div className="container-fluid page-body-wrapper">
        <SASidebar />
        <div className="main-panel">
          <div className="content-wrapper">
            <div className="col-lg-12 grid-margin stretch-card">
              <div className="card">
                <div className="card-body">
                  {/* Header Section */}
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mb-4">
                    <h4 className="card-title mb-3 mb-md-0">
                      Banner Management
                    </h4>

                    {/* Search and Filter */}
                    <div className="d-flex flex-column flex-md-row align-items-center gap-3 w-100 w-md-auto">
                      {/* Search */}
                      <div
                        className="flex-grow-1 me-md-2 position-relative"
                        style={{ maxWidth: "500px" }}
                      >
                        <input
                          type="text"
                          className="form-control form-control-sm ps-5 rounded-pill border-0"
                          placeholder="Search by Title, Description or Location"
                          onChange={handleSearchChange}
                          style={{
                            height: "38px",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                            transition: "all 0.3s ease",
                            backgroundColor: "#f8f9fa",
                          }}
                          onFocus={(e) => {
                            e.target.style.boxShadow =
                              "0 2px 12px rgba(0,0,0,0.15)";
                            e.target.style.backgroundColor = "#fff";
                          }}
                          onBlur={(e) => {
                            e.target.style.boxShadow =
                              "0 2px 8px rgba(0,0,0,0.08)";
                            e.target.style.backgroundColor = "#f8f9fa";
                          }}
                        />
                        <i className="fas fa-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                      </div>

                      {/* Filter */}
                      <div className="d-flex align-items-center">
                        <i className="fas fa-filter me-2 text-muted"></i>
                        <select
                          className="form-select"
                          value={state.filterStatus}
                          onChange={handleFilterChange}
                          style={{ minWidth: "120px" }}
                        >
                          <option value="all">All Status</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </div>

                      {/* Add Banner Button */}
                      <button
                        className="btn btn-primary d-flex align-items-center"
                        data-bs-toggle="modal"
                        data-bs-target="#bannerModal"
                        onClick={resetForm}
                      >
                        <i className="fas fa-plus me-2"></i>
                        <span className="d-none d-md-inline">Add Banner</span>
                      </button>
                    </div>
                  </div>

                  {/* Banner Table */}
                  <div className="table-responsive">
                    {state.loading ? (
                      <div
                        className="d-flex justify-content-center align-items-center"
                        style={{ height: "200px" }}
                      >
                        <RotatingLines color="#4e73df" size={50} />
                        <p className="mt-3 ms-3">Loading banners...</p>
                      </div>
                    ) : (
                      <>
                        <table className="table table-hover table-striped">
                          <thead className="table-primary">
                            <tr>
                              <th width="5%">ID</th>
                              <th width="20%">Title</th>
                              <th width="25%">Description</th>
                              <th width="15%">Location</th>
                              <th width="15%">Page Location</th>
                              <th width="10%">Status</th>
                              <th width="10%" className="text-end">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayedBanners.length > 0 ? (
                              displayedBanners.map((item) => (
                                <tr key={item.bannerId}>
                                  <td>{item.bannerId}</td>
                                  <td>{item.bannerTitle}</td>
                                  <td>{item.bannerDescription}</td>
                                  <td>{item.bannerLocation}</td>
                                  <td>{item.bannerPageLocation}</td>
                                  <td
                                    style={{
                                      color: item.bannerStatus
                                        ? "green"
                                        : "red",
                                    }}
                                  >
                                    {item.bannerStatus ? "Active" : "Inactive"}
                                  </td>
                                  <td className="text-end">
                                    <div className="d-flex justify-content-end gap-2">
                                      <button
                                        className="btn btn-sm btn-outline-primary"
                                        data-bs-toggle="modal"
                                        data-bs-target="#bannerModal"
                                        onClick={() => handleEdit(item)}
                                        title="Edit"
                                      >
                                        <i className="fas fa-edit"></i>
                                      </button>
                                      <button
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() =>
                                          handleDelete(item.bannerId)
                                        }
                                        title="Delete"
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="7" className="text-center py-4">
                                  <div className="d-flex flex-column align-items-center">
                                    <img
                                      src="https://img.icons8.com/ios/50/000000/no-camera--v1.png"
                                      alt="No banners"
                                      style={{ opacity: 0.5, width: "60px" }}
                                    />
                                    <p className="mt-3 text-muted">
                                      No banners found
                                    </p>
                                    <button
                                      className="btn btn-sm btn-primary mt-2"
                                      data-bs-toggle="modal"
                                      data-bs-target="#bannerModal"
                                      onClick={resetForm}
                                    >
                                      Add New Banner
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>

                        {/* Pagination */}
                        {displayedBanners.length > 0 && (
                          <ReactPaginate
                            previousLabel={
                              <i className="fas fa-chevron-left"></i>
                            }
                            nextLabel={<i className="fas fa-chevron-right"></i>}
                            pageCount={pageCount}
                            onPageChange={handlePageChange}
                            containerClassName={
                              "pagination justify-content-center mt-4"
                            }
                            pageClassName={"page-item"}
                            pageLinkClassName={"page-link"}
                            previousClassName={"page-item"}
                            previousLinkClassName={"page-link"}
                            nextClassName={"page-item"}
                            nextLinkClassName={"page-link"}
                            activeClassName={"active"}
                            breakLabel={"..."}
                            marginPagesDisplayed={2}
                            pageRangeDisplayed={5}
                            forcePage={state.currentPage}
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Banner Modal */}
      <div
        className="modal fade"
        id="bannerModal"
        tabIndex="-1"
        aria-labelledby="bannerModalLabel"
        aria-hidden="true"
        ref={modalRef}
      >
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header bg-light">
              <h5 className="modal-title" id="bannerModalLabel">
                {state.formData.bannerId > 0 ? "Edit Banner" : "Add New Banner"}
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={resetForm}
              ></button>
            </div>
            <div className="modal-body">
              <form>
                  <div className="row mb-3">
                  <div className="col-md-6">
                    <label
                      htmlFor="bannerTitle"
                      className="form-label required"
                    >
                      Title
                    </label>
                    <input
                      type="text"
                      className="form-control p-2"
                      id="bannerTitle"
                      name="bannerTitle"
                      value={state.formData.bannerTitle}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="col-md-5 flex-grow-1">
                    <label
                      htmlFor="bannerLocation"
                      className="form-label required"
                    >
                      Location
                    </label>
                    <select
                      className="form-select"
                      id="bannerLocation"
                      name="bannerLocation"
                      value={state.formData.bannerLocation}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Location</option>
                      {bannerLocations.map((loc) => (
                        <option key={loc} value={loc}>
                          {loc}
                        </option>
                      ))}
                    </select>
                  </div>
                  </div>
                  <div className="row mb-3">
                  <div className="col-md-6">
                    <label
                      htmlFor="bannerPageLocation"
                      className="form-label required"
                    >
                      Page Location
                    </label>
                    <select
                      className="form-select"
                      id="bannerPageLocation"
                      name="bannerPageLocation"
                      value={state.formData.bannerPageLocation}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="">Select Page Location</option>
                      {bannerPageLocations.map((loc) => (
                        <option key={loc} value={loc}>
                          {loc}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-5 flex-grow-1">
                    <label htmlFor="bannerLink" className="form-label">
                      Link URL
                    </label>
                    <input
                      type="url"
                      className="form-control p-2"
                      id="bannerLink"
                      name="bannerLink"
                      value={state.formData.bannerLink}
                      onChange={handleInputChange}
                      placeholder="https://example.com"
                    />
                  </div>
                  </div>
                  <div className="row mb-3">
                  <div className="col-12">
                    <label htmlFor="bannerDescription" className="form-label">
                      Description
                    </label>
                    <textarea
                      className="form-control"
                      id="bannerDescription"
                      name="bannerDescription"
                      rows="3"
                      value={state.formData.bannerDescription}
                      onChange={handleInputChange}
                    />
                  </div>
                  </div>
                  <div className="row mb-3">
                  <div className="col-md-6">
                    <label htmlFor="formFile" className="form-label required">
                      {state.formData.bannerId > 0
                        ? "Replace Image"
                        : "Upload Image"}
                    </label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      className="form-control"
                      id="formFile"
                      name="formFile"
                      onChange={handleInputChange}
                      accept="image/*"
                      required={state.formData.bannerId === 0}
                    />
                    <small className="text-muted">
                      Recommended size: 1200x400px
                    </small>
                  </div>

                  <div className="col-md-5 flex-grow-1">
                    <label htmlFor="bannerStatus" className="form-label">
                      Status
                    </label>
                    <select
                      className="form-select "
                      id="bannerStatus"
                      name="bannerStatus"
                      value={state.formData.bannerStatus}
                      onChange={handleInputChange}
                    >
                      <option value={true}>Active</option>
                      <option value={false}>Inactive</option>
                    </select>
                  </div>
                  </div>
                  {state.formData.bannerPath && !state.formData.formFile && (
                    <div className="col-12">
                      <div className="current-image-preview">
                        <label className="form-label">Current Image:</label>
                        <div className="mt-2 border p-2 rounded text-center">
                          <img
                            src={API_BannerURL + state.formData.bannerPath}
                            alt="Current banner"
                            className="img-fluid"
                            style={{ maxHeight: "150px" }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
              </form>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
                onClick={resetForm}
                disabled={state.formSubmitting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleFormSubmit}
                disabled={state.formSubmitting}
              >
                {state.formSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Submitting...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
